import { z } from "zod";
import Decimal from "decimal.js";
import {
  DimensionUnitSchema,
  HydratedItemSchema,
  ItemUnitSchema,
  WeightUnitSchema,
} from "./items";
import { AddressSchema, HydratedCompanySchema } from "./companies";
import { HydratedOpportunitySchema } from "./opportunities";
import { isOpportunityOwner, OpportunityOwnership } from "./permissions";
import { LocationSchema } from "./locations";
import { PersonSchema } from "./people";
import {
  PriceListSchema,
  PricingUnitSchema,
  QuoteUnitPriceResolutionSchema,
} from "./priceLists";
import { QUOTE_TEMPLATE_IDS } from "../quotes";
import {
  CustomFieldsSchemaSchema,
  CustomFieldValuesSchema,
} from "./customFields";
import { CurrencyCodeSchema, ExchangeRateSnapshotSchema } from "./currency";
import { DateOnlyStringSchema } from "./dates";
import { QuoteDocumentSchema } from "./quoteDocuments";

export const QuoteTemplateIdSchema = z.enum(QUOTE_TEMPLATE_IDS);

/** Rounding direction applied to displayed quote prices. */
export const RoundingModeSchema = z.enum(["UP", "DOWN", "NEAREST"]);
/** Rounding direction applied to displayed quote prices. */
export type RoundingMode = z.infer<typeof RoundingModeSchema>;

/**
 * Returns true when the string parses as a finite, positive decimal.
 * @param value - The candidate increment string
 * @returns Whether the value is a valid rounding increment
 */
const isPositiveDecimalString = (value: string): boolean => {
  try {
    const parsed = new Decimal(value);
    return parsed.isFinite() && parsed.greaterThan(0);
  } catch {
    return false;
  }
};

/** Configurable rounding applied to displayed quote prices after FX conversion. */
export const QuotePriceRoundingSchema = z.object({
  mode: RoundingModeSchema,
  /** Rounding step as a positive decimal string: "0.01" cents, "1" whole dollar, "5" nearest $5. */
  increment: z
    .string()
    .refine(
      isPositiveDecimalString,
      "Increment must be a positive decimal number",
    ),
  /** Decimal places to display. */
  decimals: z.number().int().min(0).max(4),
});
/** Configurable rounding applied to displayed quote prices after FX conversion. */
export type QuotePriceRounding = z.infer<typeof QuotePriceRoundingSchema>;

/** Default rounding config applied when a user turns rounding on. */
export const DEFAULT_QUOTE_PRICE_ROUNDING: QuotePriceRounding = {
  mode: "NEAREST",
  increment: "0.01",
  decimals: 2,
};

/** Human-readable labels for each rounding mode. */
export const ROUNDING_MODE_LABELS: Record<RoundingMode, string> = {
  NEAREST: "Nearest",
  UP: "Round up",
  DOWN: "Round down",
};

/** Preset rounding increments offered in the quote settings UI. */
export const QUOTE_PRICE_ROUNDING_INCREMENT_OPTIONS: {
  value: string;
  label: string;
}[] = [
  { value: "0.01", label: "$0.01" },
  { value: "0.05", label: "$0.05" },
  { value: "0.10", label: "$0.10" },
  { value: "0.25", label: "$0.25" },
  { value: "0.50", label: "$0.50" },
  { value: "1", label: "$1" },
  { value: "5", label: "$5" },
  { value: "10", label: "$10" },
  { value: "100", label: "$100" },
  { value: "500", label: "$500" },
  { value: "1000", label: "$1,000" },
];

// Quote Enums
export const QuoteStatusSchema = z.enum(["DRAFT", "ARCHIVED", "FINALIZED"]);
export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;
export const QuoteStatusEnum = QuoteStatusSchema.enum;

export const QUOTE_STATUS_DISPLAY_NAMES = {
  [QuoteStatusEnum.DRAFT]: "Draft",
  [QuoteStatusEnum.ARCHIVED]: "Archived",
  [QuoteStatusEnum.FINALIZED]: "Finalized",
} as const satisfies Record<QuoteStatus, string>;

export const QuoteLineTypeSchema = z.enum([
  "ITEM",
  "SECTION",
  "CUSTOM",
  "DISCOUNT",
  "NOTE",
]);
export type QuoteLineType = z.infer<typeof QuoteLineTypeSchema>;
export const QuoteLineTypeValues = QuoteLineTypeSchema.enum;

/**
 * A quote discount: PERCENTAGE applies to the item subtotal; the AMOUNT
 * variant's `amount` is denominated in the quote's display currency (the quote
 * currency when the quote converts away from the org base, else the org base
 * currency), with base-currency equivalents derived at the pinned snapshot.
 */
export const QuoteDiscountSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("PERCENTAGE"),
    percentage: z.string(),
  }),
  z.object({
    type: z.literal("AMOUNT"),
    amount: z.string(),
  }),
]);
export type QuoteDiscount = z.infer<typeof QuoteDiscountSchema>;

/**
 * A quantity break on an item line. `unitPriceOverride` is denominated in the
 * quote's display currency (the quote currency when the quote converts away
 * from the org base, else the org base currency) and is rendered verbatim to
 * the customer; base-currency equivalents are derived at the pinned snapshot.
 */
export const QuantityBreakSchema = z.object({
  quantity: z.string(),
  unitPriceOverride: z.string().optional(),
});
export type QuantityBreak = z.infer<typeof QuantityBreakSchema>;

export const HydratedQuantityBreakSchema = z.object({
  quantity: z.string(),
  unitPriceOverride: z.string().nullable(),
  unitPriceSource: QuoteUnitPriceResolutionSchema.nullable().optional(),
  priceListLineId: z.string().nullable().optional(),
  unitPrice: z.string().nullable(),
  unitCost: z.string().nullable().optional(),
  amount: z.string().nullable(),
  convertedUnitPrice: z.string().nullable().optional(),
  convertedAmount: z.string().nullable().optional(),
  totalWeight: z.string().nullable(),
  totalWeightUnit: WeightUnitSchema.nullable().optional(),
  totalLength: z.string().nullable(),
  totalWidth: z.string().nullable(),
  totalHeight: z.string().nullable(),
  pricingUnit: PricingUnitSchema.nullable().optional(),
  conversionFactor: z.string().nullable().optional(),
  convertedFromUnitPrice: z.string().nullable().optional(),
});
export type HydratedQuantityBreak = z.infer<typeof HydratedQuantityBreakSchema>;

const ItemOverrideValueSchema = z
  .string()
  .refine(isPositiveDecimalString, "Must be a positive decimal number");

/**
 * Measurements per each that apply to one quote line only, independently of
 * the item or line quantity unit. Sizes are in the line's
 * `dimensionUnitOverride`, else the item's dimension unit; weight is in the
 * item's weight unit.
 */
export const QuoteLineItemOverridesSchema = z.object({
  length: ItemOverrideValueSchema.optional(),
  width: ItemOverrideValueSchema.optional(),
  height: ItemOverrideValueSchema.optional(),
  weight: ItemOverrideValueSchema.optional(),
  allowance: ItemOverrideValueSchema.optional(),
});
/**
 * Item field values that apply to one quote line only, with sizes in the
 * line's dimension unit and weight in the item's weight unit.
 */
export type QuoteLineItemOverrides = z.infer<
  typeof QuoteLineItemOverridesSchema
>;

/** One item field a quote line can override. */
export const ItemOverrideFieldSchema = QuoteLineItemOverridesSchema.keyof();
/** One item field a quote line can override. */
export type ItemOverrideField = z.infer<typeof ItemOverrideFieldSchema>;
/** Every item field a quote line can override. */
export const ITEM_OVERRIDE_FIELDS = ItemOverrideFieldSchema.options;

/** Which fields a quote line may override, keyed by field. */
export const ItemOverrideFieldSelectionSchema = z.object({
  length: z.boolean(),
  width: z.boolean(),
  height: z.boolean(),
  weight: z.boolean(),
  allowance: z.boolean(),
  dimensionUnit: z.boolean().default(true),
}) satisfies z.ZodType<Record<ItemOverrideField, boolean>>;
/** Which fields a quote line may override, keyed by field. */
export type ItemOverrideFieldSelection = z.infer<
  typeof ItemOverrideFieldSelectionSchema
>;
/** One field an admin can enable or disable for quote line overrides. */
export type ItemOverrideSelectionField = keyof ItemOverrideFieldSelection;
/** Every field an admin can enable or disable, in schema order. */
export const ITEM_OVERRIDE_SELECTION_FIELDS =
  ItemOverrideFieldSelectionSchema.keyof().options;
/** The selection a new quote settings row starts with: every field enabled. */
export const DEFAULT_ITEM_OVERRIDE_FIELD_SELECTION: ItemOverrideFieldSelection =
  {
    length: true,
    width: true,
    height: true,
    weight: true,
    allowance: true,
    dimensionUnit: true,
  };

type ItemOverrideSettings =
  | {
      allowItemFieldOverrides: boolean;
      itemOverrideFields: ItemOverrideFieldSelection;
    }
  | null
  | undefined;

/**
 * Returns whether a quote line may override one item field: the switch is on
 * and the field is selected.
 */
export function isItemOverrideFieldEnabled(
  settings: ItemOverrideSettings,
  field: ItemOverrideSelectionField,
): boolean {
  return (
    settings?.allowItemFieldOverrides === true &&
    settings.itemOverrideFields[field]
  );
}

/**
 * Returns the item fields a quote line may override: none when the switch is
 * off or no settings row exists, otherwise the selected fields.
 */
export function enabledItemOverrideFields(
  settings: ItemOverrideSettings,
): ItemOverrideField[] {
  return ITEM_OVERRIDE_FIELDS.filter((field) =>
    isItemOverrideFieldEnabled(settings, field),
  );
}

/**
 * Returns the item fields a quote line may override, leaving out allowance
 * unless the item setting enables the allowance dimension.
 */
export function availableItemOverrideFields(
  settings: ItemOverrideSettings,
  enableAllowance: boolean,
): ItemOverrideField[] {
  return enabledItemOverrideFields(settings).filter(
    (field) => field !== "allowance" || enableAllowance,
  );
}

// Quote Line Types - id is required on all lines
// Frontend generates id on creation, backend generates for agent actions
export const QuoteItemLineSchema = z.object({
  id: z.string(),
  type: z.literal("ITEM"),
  itemId: z.string().nullable(),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
  quantityBreaks: z.array(QuantityBreakSchema),
  confidenceScore: z.number().min(0).max(1).nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
  itemOverrides: QuoteLineItemOverridesSchema.nullable().optional(),
  unitOverride: ItemUnitSchema.nullable().optional(),
  dimensionUnitOverride: DimensionUnitSchema.nullable().optional(),
});
export type QuoteItemLine = z.infer<typeof QuoteItemLineSchema>;

export const HydratedQuoteItemLineSchema = z.object({
  id: z.string(),
  type: z.literal("ITEM"),
  itemId: z.string().nullable(),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
  quantityBreaks: z.array(HydratedQuantityBreakSchema),
  item: HydratedItemSchema.nullable(),
  effectiveItem: HydratedItemSchema.nullable().optional(),
  amount: z.string().nullable(),
  confidenceScore: z.number().min(0).max(1).nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
  itemOverrides: QuoteLineItemOverridesSchema.nullable().optional(),
  unitOverride: ItemUnitSchema.nullable().optional(),
  dimensionUnitOverride: DimensionUnitSchema.nullable().optional(),
  unit: ItemUnitSchema.nullable().optional(),
});

export type HydratedQuoteItemLine = z.infer<typeof HydratedQuoteItemLineSchema>;

export const QuoteSectionLineSchema = z.object({
  id: z.string(),
  type: z.literal("SECTION"),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
});
export type QuoteSectionLine = z.infer<typeof QuoteSectionLineSchema>;

export const QuoteNoteLineSchema = z.object({
  id: z.string(),
  type: z.literal("NOTE"),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
});
export type QuoteNoteLine = z.infer<typeof QuoteNoteLineSchema>;

export const QuoteDiscountLineSchema = z.object({
  id: z.string(),
  type: z.literal("DISCOUNT"),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
  discount: QuoteDiscountSchema,
});
export type QuoteDiscountLine = z.infer<typeof QuoteDiscountLineSchema>;

/**
 * A discount line as returned on a hydrated quote: the stored config plus the
 * server-computed row amounts, mirroring how item lines carry `amount` and
 * `convertedAmount`. `amount` is the base-currency discount value and
 * `convertedAmount` the presentation-currency value the totals subtract (equal
 * for base-currency quotes). Both are null when the subtotal cannot be
 * computed (an item line lacks an amount), and optional so client-side
 * constructors of hydrated lines (e.g. unsaved preview state) can omit them.
 */
export const HydratedQuoteDiscountLineSchema = QuoteDiscountLineSchema.extend({
  amount: z.string().nullable().optional(),
  convertedAmount: z.string().nullable().optional(),
});
export type HydratedQuoteDiscountLine = z.infer<
  typeof HydratedQuoteDiscountLineSchema
>;

export const QuoteLineSchema = z.discriminatedUnion("type", [
  QuoteItemLineSchema,
  QuoteSectionLineSchema,
  QuoteNoteLineSchema,
  QuoteDiscountLineSchema,
]);
export type QuoteLine = z.infer<typeof QuoteLineSchema>;

export const HydratedQuoteLineSchema = z.discriminatedUnion("type", [
  HydratedQuoteItemLineSchema,
  QuoteSectionLineSchema,
  QuoteNoteLineSchema,
  HydratedQuoteDiscountLineSchema,
]);
export type HydratedQuoteLine = z.infer<typeof HydratedQuoteLineSchema>;

export const QuoteOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  address: AddressSchema.nullable(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  website: z.string().nullable(),
});
export type QuoteOrganization = z.infer<typeof QuoteOrganizationSchema>;

export const QuoteSignatureSchema = z.object({
  name: z.string(),
  email: z.string(),
  ipAddress: z.string(),
  signedAt: z.coerce.date(),
});
export type QuoteSignature = z.infer<typeof QuoteSignatureSchema>;

// API Request/Response Types
export const CreateQuoteParamsSchema = z.object({
  name: z.string().optional(),
  deliveryTerms: z.string().optional(),
  paymentTerms: z.string().optional(),
  termsOfService: z.string().optional(),
  quoteFooter: z.string().optional(),
  expirationDate: DateOnlyStringSchema.nullable().optional(),
  quoteDate: DateOnlyStringSchema.nullable().optional(),
  customerCompanyId: z.string().nullable().optional(),
  priceListId: z.string().nullable().optional(),
  shippingAddressId: z.string().nullable().optional(),
  locationId: z.string().nullable().optional(),
  lines: z.array(QuoteLineSchema).optional(),
  requiresCustomerSignature: z.boolean().optional(),
  opportunityId: z.string().nullable().optional(),
  salesRepUserId: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
  publicAccessRevoked: z.boolean().optional(),
  coverLetterMarkdown: z.string().nullable().optional(),
  quoteCurrency: CurrencyCodeSchema.nullable().optional(),
  exchangeRateId: z.string().nullable().optional(),
  personIds: z.array(z.string()).optional(),
});
export type CreateQuoteParams = z.infer<typeof CreateQuoteParamsSchema>;

export const UpdateQuoteParamsSchema = z.object({
  name: z.string().optional(),
  deliveryTerms: z.string().optional(),
  paymentTerms: z.string().optional(),
  termsOfService: z.string().optional(),
  quoteFooter: z.string().optional(),
  expirationDate: DateOnlyStringSchema.nullable().optional(),
  quoteDate: DateOnlyStringSchema.nullable().optional(),
  customerCompanyId: z.string().nullable().optional(),
  priceListId: z.string().nullable().optional(),
  shippingAddressId: z.string().nullable().optional(),
  locationId: z.string().nullable().optional(),
  lines: z.array(QuoteLineSchema).optional(),
  autoCreateRevision: z.boolean().optional(),
  showTotals: z.boolean().optional(),
  opportunityId: z.string().nullable().optional(),
  salesRepUserId: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
  publicAccessRevoked: z.boolean().optional(),
  archived: z.boolean().optional(),
  coverLetterMarkdown: z.string().nullable().optional(),
  quoteCurrency: CurrencyCodeSchema.nullable().optional(),
  exchangeRateId: z.string().nullable().optional(),
  personIds: z.array(z.string()).optional(),
});
export type UpdateQuoteParams = z.infer<typeof UpdateQuoteParamsSchema>;

export const UpdateQuoteStatusParamsSchema = z.object({
  status: QuoteStatusSchema,
});
export type UpdateQuoteStatusParams = z.infer<
  typeof UpdateQuoteStatusParamsSchema
>;

/**
 * The quote-settings pointer naming the quote-document template new quotes seed
 * their document from; null clears the org default.
 */
export const DefaultQuoteDocumentTemplateIdSchema = z
  .string()
  .min(1)
  .nullable();

/** The org-default quote-document template id, or null when unset. */
export type DefaultQuoteDocumentTemplateId = z.infer<
  typeof DefaultQuoteDocumentTemplateIdSchema
>;

export const GetQuoteByNumberParamsSchema = z.object({
  quoteNumber: z.number(),
  organizationId: z.string(),
  revision: z.number().optional(),
});
export type GetQuoteByNumberParams = z.infer<
  typeof GetQuoteByNumberParamsSchema
>;

export const CreateRevisionParamsSchema = z.object({
  quoteId: z.string(),
  organizationId: z.string(),
});
export type CreateRevisionParams = z.infer<typeof CreateRevisionParamsSchema>;

// Filter for searching quotes
export const QuoteFilterSchema = z.object({
  status: QuoteStatusSchema.optional(),
  statuses: z.array(QuoteStatusSchema).optional(),
  search: z.string().optional(),
  ids: z.array(z.string()).optional(),
  quoteNumbers: z.array(z.number()).optional(),
  opportunityId: z.string().optional(),
  unlinkedOnly: z.boolean().optional(),
  createdAfter: z.coerce.date().optional(),
  createdBefore: z.coerce.date().optional(),
  updatedAfter: z.coerce.date().optional(),
  updatedBefore: z.coerce.date().optional(),
  expirationDateAfter: z.coerce.date().optional(),
  expirationDateBefore: z.coerce.date().optional(),
  includeArchived: z.boolean().optional(),
  itemIds: z.array(z.string()).optional(),
  onlyLatestFinalizedRevision: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});
export type QuoteFilter = z.infer<typeof QuoteFilterSchema>;

export const ListQuotesQuerySchema = z.object({
  status: QuoteStatusSchema.optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});
export type ListQuotesQuery = z.infer<typeof ListQuotesQuerySchema>;

// Response Types
export const CalculateQuoteTotalsResultSchema = z.object({
  subtotal: z.string().nullable(),
  subtotalWithDiscounts: z.string().nullable(),
  total: z.string().nullable(),
});
export type CalculateQuoteTotalsResult = z.infer<
  typeof CalculateQuoteTotalsResultSchema
>;

// Quote Response Schema (for API responses)
export const QuoteResponseSchema = z.object({
  id: z.string(),
  status: QuoteStatusSchema,
  quoteNumber: z.number(),
  quoteDisplayNumber: z.string(),
  revision: z.number(),
  isLatestRevision: z.boolean(),
  latestRevisionId: z.string().nullable().optional(),
  name: z.string().nullable(),
  deliveryTerms: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  termsOfService: z.string().nullable(),
  expirationDate: DateOnlyStringSchema.nullable(),
  quoteDate: DateOnlyStringSchema.nullable(),
  customerCompanyId: z.string().nullable(),
  priceListId: z.string().nullable().optional(),
  shippingAddressId: z.string().nullable().optional(),
  shippingAddress: AddressSchema.nullable().optional(),
  locationId: z.string().nullable().optional(),
  location: LocationSchema.nullable().optional(),
  requiresCustomerSignature: z.boolean(),
  showTotals: z.boolean(),
  showTotalWeight: z.boolean().optional(),
  isSigned: z.boolean().optional(),
  signature: QuoteSignatureSchema.nullable().optional(),
  lines: z.array(HydratedQuoteLineSchema).optional(),
  itemsDeepCopy: z.record(z.string(), z.any()).optional(),
  subtotal: z.string().nullable(),
  subtotalWithDiscounts: z.string().nullable(),
  total: z.string().nullable(),
  totalWeight: z.string().nullable(),
  totalWeightUnit: WeightUnitSchema.nullable(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  organization: QuoteOrganizationSchema.nullable().optional(),
  customerCompany: HydratedCompanySchema.nullable().optional(),
  people: z.array(PersonSchema).optional(),
  priceList: PriceListSchema.nullable().optional(),
  publicQuoteTemplate: QuoteTemplateIdSchema.nullable().optional(),
  quotePriceRounding: QuotePriceRoundingSchema.nullable().optional(),
  quoteFooter: z.string().nullable().optional(),
  publicAccessRevokedAt: z.coerce.date().nullable().optional(),
  archivedAt: z.coerce.date().nullable().optional(),
  opportunityId: z.string().nullable().optional(),
  opportunity: HydratedOpportunitySchema.nullable().optional(),
  salesRepUserId: z.string().nullable().optional(),
  salesRepName: z.string().nullable().optional(),
  salesRepEmail: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.nullable().optional(),
  customFieldsSchema: CustomFieldsSchemaSchema.nullable().optional(),
  coverLetterMarkdown: z.string().nullable().optional(),
  enableCoverLetter: z.boolean().optional(),
  quoteCurrency: CurrencyCodeSchema.nullable().optional(),
  exchangeRateId: z.string().nullable().optional(),
  baseCurrency: CurrencyCodeSchema.nullable().optional(),
  snapshot: ExchangeRateSnapshotSchema.nullable().optional(),
  /**
   * The validated (sanitized) HTML document to render this quote, present only
   * when the owning org has the `quote-documents` flag on and the quote has a
   * stored, render-valid document. Absent means the legacy editor preview
   * renders instead, so flag-off behavior is unchanged.
   */
  quoteDocument: QuoteDocumentSchema.optional(),
  /**
   * Signed, org-scoped asset URLs keyed by the document's `asset-id` values,
   * accompanying {@link QuoteResponseSchema}'s `quoteDocument`.
   */
  assetUrls: z.record(z.string(), z.string()).optional(),
});
export type QuoteResponse = z.infer<typeof QuoteResponseSchema>;

/**
 * The fields of a quote that determine ownership: its own sales rep, plus the
 * associated opportunity's owners (sales rep and estimators) when hydrated.
 */
export type QuoteOwnership = Pick<QuoteResponse, "salesRepUserId"> & {
  opportunity?: OpportunityOwnership | null;
};

/**
 * Whether the given user owns a quote: they are its sales rep, or they own
 * the associated opportunity (its sales rep or one of its estimators).
 * @param userId - The user id to test
 * @param quote - The quote's ownership fields
 * @returns True when the user owns the quote
 */
export const isQuoteOwner = (userId: string, quote: QuoteOwnership): boolean =>
  (!!quote.salesRepUserId && quote.salesRepUserId === userId) ||
  isOpportunityOwner(userId, quote.opportunity ?? {});

// Standard API Response Wrappers
export const CreateQuoteResponseSchema = z.object({
  quote: QuoteResponseSchema,
});
export type CreateQuoteResponse = z.infer<typeof CreateQuoteResponseSchema>;

export const ListQuotesResponseSchema = z.object({
  quotes: z.array(QuoteResponseSchema),
  total: z.number(),
});
export type ListQuotesResponse = z.infer<typeof ListQuotesResponseSchema>;

export const DeleteQuoteResponseSchema = z.object({
  success: z.boolean(),
});
export type DeleteQuoteResponse = z.infer<typeof DeleteQuoteResponseSchema>;

// Error Response Schema
export const QuoteErrorResponseSchema = z.object({
  error: z.string(),
});
export type QuoteErrorResponse = z.infer<typeof QuoteErrorResponseSchema>;
